/**
 * i18next bootstrap (issue #140 PR #1).
 *
 * Behaviour:
 *   - Loads translations from ConfigService at runtime via an http-backend
 *     that points at `<apiGateway>/config/<store>/translations/<locale>.json`.
 *   - Falls back en-US for any key missing in the active locale; a missing
 *     key at the bottom of the chain logs a `console.warn` so QA can see
 *     the gap in the browser devtools.
 *   - Registers the `keyDisplay` post-processor for the `?i18n=keys` /
 *     `?i18n=missing` debug query-param mode.
 *
 * Wiring:
 *   - `App.tsx` imports this module for the side effect of
 *     `i18next.init(...)`. The init is asynchronous; consumers should
 *     wait for `i18n.isInitialized` (handled implicitly by the Suspense
 *     boundary around `<LocaleSwitcher />`).
 *   - `fetchRuntimeConfig()` runs first so the api-gateway URL + active
 *     store label are known when this module wires up the backend.
 *
 * NOTE: The locales catalog (default + supported list + switcherStyle) is
 * fetched separately by `useLocaleViewModel` on first render — it isn't
 * needed at i18n init time, only when the LocaleSwitcher renders.
 */

import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { keyDisplayPostProcessor } from "./keyDisplayPostProcessor";
import { fetchRuntimeConfig } from "../services/config";
import { createConfigServiceBackend } from "./translationsBackend";

// Re-exported for compatibility — the ConfigService translations URL builder
// now lives in the side-effect-free ./translationsBackend module (issue #292).
export { buildTranslationsUrl } from "./translationsBackend";

const STORAGE_KEY = "intersum.locale";
const FALLBACK_LOCALE = "en-US";

/**
 * Resolve the active locale at boot:
 *   1. localStorage["intersum.locale"] if set (user's prior choice)
 *   2. otherwise the store's documented default (loaded from runtime-config)
 *   3. otherwise the global fallback (en-US)
 */
function resolveBootLocale(storeDefault: string | undefined): string {
	if (typeof window !== "undefined") {
		try {
			const stored = window.localStorage.getItem(STORAGE_KEY);
			if (stored && /^[a-z]{2}-[A-Z]{2}$/.test(stored)) {
				return stored;
			}
		} catch {
			// localStorage may throw in some browser privacy modes; ignore.
		}
	}
	return storeDefault ?? FALLBACK_LOCALE;
}

/**
 * Reduce a tenant-prefixed Medusa base URL to the bare api-gateway root.
 *
 * `config.medusa.backendUrl` is `<gateway>/api/v1/tenants/<prefix>` (see
 * config.ts `withTenantPrefix`). The ConfigService translations endpoint lives
 * at the gateway ROOT (`<gateway>/config/<label>/translations/...`), NOT under
 * the tenant-prefixed Medusa path — so the whole `/api/v1/tenants/<prefix>`
 * segment must be stripped. (The old regex only stripped a trailing `/store`,
 * which `backendUrl` doesn't carry, so the tenant prefix leaked into the URL
 * and every bundle 404'd — issue #140.)
 */
export function resolveGatewayRoot(backendUrl: string): string {
	return (backendUrl ?? "")
		.replace(/\/api\/v1\/tenants\/[^/]+.*$/, "")
		.replace(/\/store\/?$/, "")
		.replace(/\/+$/, "");
}

let bootstrapPromise: Promise<typeof i18next> | null = null;

/**
 * Idempotently initialise i18next. Multiple imports of this module return
 * the same in-flight promise so the storefront only fires a single backend
 * fetch on cold start.
 */
export function bootstrapI18n(): Promise<typeof i18next> {
	if (bootstrapPromise) {
		return bootstrapPromise;
	}

	bootstrapPromise = (async () => {
		const config = await fetchRuntimeConfig();
		// Reduce the tenant-prefixed Medusa base to the gateway root, then key the
		// translations path on the tenant prefix (folder label), not storeLabel.
		const apiGatewayUrl: string = resolveGatewayRoot(config.medusa?.backendUrl ?? "");
		const tenantLabel: string =
			config.tenantPrefix ?? config.storeLabel ?? "intersum-ecommerce-example";

		// Boot directly into the store's default locale (from the runtime-config
		// `locales` block) so init() loads the correct bundle up front. Booting
		// at en-US and letting useLocaleViewModel flip to e.g. sv-SE afterwards
		// needs a second async bundle fetch that races first paint — the pill
		// paints the en-US value ("ENG") and only the catalog-driven currency
		// updates, never the text. A user's prior localStorage pick still wins
		// (resolveBootLocale checks it first).
		const initialLocale = resolveBootLocale(config.locales?.default);

		await i18next
			.use(createConfigServiceBackend(apiGatewayUrl, tenantLabel))
			.use(keyDisplayPostProcessor)
			.use(initReactI18next)
			.init({
				lng: initialLocale,
				fallbackLng: FALLBACK_LOCALE,
				ns: ["default"],
				defaultNS: "default",
				postProcess: ["keyDisplay"],
				interpolation: { escapeValue: false },
				returnEmptyString: false,
				// saveMissing must be true for i18next to invoke missingKeyHandler
				// (AC-7: warn on missing keys). We don't write missing keys
				// anywhere — the handler only logs.
				saveMissing: true,
				// No <Suspense> boundary wraps the switcher / MobileNav, so a
				// suspending useTranslation would unmount the subtree mid-render.
				// Render with the key (or fallback) until the bundle resolves —
				// but react-i18next's defaults (bindI18nStore: '') do NOT re-render
				// when an async backend bundle is *added* to the store. With our
				// ConfigService http-backend the bundle loads after init resolves,
				// so without binding the store events the switcher stays on the
				// en-US fallback forever (e.g. tayrona paints "ENG" not "SWE").
				// Bind 'added'/'loaded' so the subtree re-renders once the locale
				// bundle lands.
				react: {
					useSuspense: false,
					bindI18n: "languageChanged loaded",
					bindI18nStore: "added removed",
				},
				missingKeyHandler: (lng, _ns, key) => {
					// eslint-disable-next-line no-console
					console.warn(`[i18n] missing key '${key}' for ${Array.isArray(lng) ? lng[0] : lng}`);
				},
			});

		return i18next;
	})();

	return bootstrapPromise;
}

// Kick off the bootstrap eagerly so the rest of the app can rely on
// react-i18next being available by the time the first render happens.
bootstrapI18n().catch((error) => {
	// eslint-disable-next-line no-console
	console.error("[i18n] bootstrap failed:", error);
});

export { i18next };
export default i18next;
